import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';
import { take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import {
  CartService,
  CustomerService,
  NoCommaPipe,
  Product,
  ProductStockService,
  ProductType,
  SRouterLinkWithHrefDirective,
  WishListApiService,
} from '@speedy/core';

import { ProductStockOptionsComponent } from '../product-stock-options/product-stock-options.component';
import { SpeedyQtyComponent } from '../speedy-qty/speedy-qty.component';
import { PriceComponent } from '../price/price.component';
import { CurrencyComponent } from '../currency/currency.component';

@Component({
  selector: 'app-product-list-item',
  templateUrl: './product-list-item.component.html',
  styleUrls: ['./product-list-item.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    SRouterLinkWithHrefDirective,
    MatIcon,
    CurrencyComponent,
    PriceComponent,
    SpeedyQtyComponent,
    MatProgressSpinner,
    ProductStockOptionsComponent,
    NoCommaPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListItemComponent {
  private readonly cartService = inject(CartService);
  private readonly wishListApiService = inject(WishListApiService);
  private readonly alertService = inject(ToastrService);
  public readonly productStockService = inject(ProductStockService);
  public readonly customerService = inject(CustomerService);

  @Input() idPrefix: 'modal' | 'related' | 'default' = 'default';
  @Input() wide = false;
  @Input() product!: Product;
  @Input() index!: number;
  @Input() isWishlistType = false;
  @Input() qty = 1;

  @Output() deleteFromWishlist = new EventEmitter<void>();
  @Output() changeQty = new EventEmitter<number>();

  ProductType = ProductType;
  loading = signal(false);

  addToCart(product: Product) {
    this.loading.set(true);

    this.cartService
      .addToCart(product, this.qty)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.alertService.error(error.error?.message ?? error.error);
        },
      });
  }

  isConfigurable() {
    if (!this.product) return false;

    return this.product.type_id === ProductType.Configurable;
  }

  onQtyChange(qty: number): void {
    this.qty = qty;
    this.changeQty.emit(qty);
  }

  addProductToWishList(productId: number): void {
    if (this.customerService.isLoggedIn()) {
      this.addToWishlist(productId);
    } else {
      this.wishListApiService.notifyUserToLogIn();
      this.wishListApiService.saveItemToWishList(productId);
    }
  }

  private addToWishlist(productId: number): void {
    this.wishListApiService
      .addToWishList(productId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertService.success('Product was added to wishlist');
          this.loading.set(false);
        },
        error: (error) => {
          this.alertService.error(error);
          this.loading.set(false);
        },
      });
  }

  deleteProductFromWishList(): void {
    this.deleteFromWishlist.emit();
  }
}
